
import { RETRY_VERB } from "./debug-constants";

// Utilities for promises with a retry policy

const STRATEGY_0 = {
  num_retries: 3,
  wait_ms: 1000,
};

const SUCCESS = "SUCCESS";
const FAIL = "FAIL";
const RETRY = "RETRY";

const sleep = (delay) =>
  new Promise(resolve => setTimeout(resolve, delay));

/**
 * @param items Iterate over these
 * @param fn: Returns a Promise function
 * @return {*}
 */
const for_each_promise = (items, fn) => {
  return items.reduce( (promise, item) => {
    return promise.then( () => {
      return fn(item);
    });
  }, Promise.resolve());
};

/**
 * @param items Iterate over these
 * @param promise_fn: Returns a Promise function
 * @param fail_retry_fn: Takes the failure as an argument, and returns
 *   true if the failure is recoverable and should retry. Else the
 *   failure is a hard fail and should stop immediately.
 * @return {*}
 */
const for_each_promise_retry = (items, promise_fn, fail_retry_fn) => {
  return items.reduce( (promise, env) => {
    return promise.then( () => {

      return new Promise((resolve, reject) => {

        if (env.status !== RETRY) {
          // We're done, just continue.
          if (RETRY_VERB) {
            console.log("RETRY ", promise_fn.name, ":", env.index, " ", env.status, "SKIP");
          }
          return resolve();
        }
        env.params._call_index = env.index;

        if (RETRY_VERB) {
          console.log("RETRY ", promise_fn.name, ":", env.index, " ", "Calling");
        }
        promise_fn(env.params)
          .then(result => {
            env.result = result;
            env.status = SUCCESS;
            return resolve();
          })
          .catch(err => {
            env.errors.push(err);
            if (!fail_retry_fn(err)) {
              if (RETRY_VERB) {
                console.log("RETRY ", promise_fn.name, ":", env.index, " HARD FAIL");
              }
              env.status = FAIL;
              return resolve();
            }
            if (RETRY_VERB) {
              console.log("RETRY ", promise_fn.name, ":", env.index, " RETRY");
            }
            // Sleep, then retry
            return sleep(env.wait_ms).then(() => {
              env.index++;
              resolve();
            })
          })
      })
    });
  }, Promise.resolve());
};


const run_promise = (params, promise_fn, fail_retry_fn, strategy) => {

  const env = {
    index: 0,
    status: RETRY,
    params: params,
    errors: [],
  };
  const items = [];

  for(let i=0; i < strategy.num_retries; i++) {
    items.push(env);
  }

  return new Promise((resolve, reject) => {
    for_each_promise_retry(items, promise_fn, fail_retry_fn)
      .then(() => {
        if (env.status === SUCCESS) {
          return resolve(env.result);
        }
        return reject(env.errors);
      })
  })
};

const promise_timeout = timeout_ms => {
  return new Promise((resolve, reject) => {
    setTimeout(() => resolve(), timeout_ms);
  })
};


/**
 * Keep executing promise_fn. If it rejects, then we wait
 * for timeout_ms and try again. As soon as it resolves, we
 * resolve the calling function.
 *
 * @param params: {Object} to be passed to the promise_fn
 * @param max_tries: {int} max tries before failing.
 * @param promise_fn: Promise function. As soon as it resolves,
 *  we resolve the function.
 * @param timeout_ms: Time to wait between calls.
 * @param try_number: {int}
 */
const promise_until = (params, max_tries, promise_fn, timeout_ms, try_number=0) => {

  return new Promise((resolve, reject) => {
    if (max_tries === 0) {
      return reject("Max tries exceeded");
    }

    params.try_number = try_number;

    return promise_fn(params)
      .then(result => {
        return resolve(result);
      })
      .catch(err => {
        // Not done yet
        return promise_timeout(timeout_ms)
          .then(() => {

            return promise_until(params, max_tries - 1, promise_fn, timeout_ms, try_number+1)
              .then(result => {
                return resolve(result);
              })
              .catch(err => {
                return reject(err);
              })
          })
      })
  });
}

export { run_promise, STRATEGY_0, promise_timeout, promise_until,
  for_each_promise };
